import { useEffect } from 'react';
import { useGLTF } from '@react-three/drei';
import { useGameStore } from '../../core/store';
import { PLAYER_KICK_GLB_URL, CAPTAIN_TSUBASA_GLB_URL } from '../characters/characterUrls';
import {
  FOOTBALL_GLB_URL,
  FOOTBALL_GOAL_GLB_URL,
} from '../shot/shotUrls';
import {
  playExitBlackVoiceover,
  preloadExitBlackVoiceover,
  preloadPitchBed,
  silenceAfterTunnelExit,
  startPitchBed,
  stopExitBlackVoiceover,
  stopExitSynth,
} from '../audio';
import { EXIT_BLACK_MS, EXIT_BLACKOUT_HOLD_MS } from '../shot/stadiumCinematic';
import { useTunnelExitStore } from './tunnelExitStore';

/**
 * Shot-asset prewarm + black/VO handoff.
 * Behind-the-model walk-out is skipped; TunnelWalker calls scheduleExitHandoff()
 * as soon as the player reaches the mouth (mobile and desktop).
 */
export const EXIT_CINEMATIC_WALK_DUR = 6.5;

let handoffLocked = false;
let handoffTimer: number | null = null;
let voTimer: number | null = null;
let shotWarmTimer: number | null = null;
let assetsPrewarmed = false;

function clearTimer(id: number | null) {
  if (id != null) window.clearTimeout(id);
}

export function resetExitHandoffLock() {
  handoffLocked = false;
  clearTimer(handoffTimer);
  clearTimer(voTimer);
  clearTimer(shotWarmTimer);
  handoffTimer = null;
  voTimer = null;
  shotWarmTimer = null;
}

/** Prefetch ShotAim chunks + GLBs during tunnel walk / black (no early phase flip). */
export function prewarmShotAssets() {
  if (assetsPrewarmed) return;
  assetsPrewarmed = true;
  void import('../shot/ShotAim');
  void import('../shot/PenaltyStadium');
  useGLTF.preload(FOOTBALL_GOAL_GLB_URL);
  useGLTF.preload(FOOTBALL_GLB_URL);
  useGLTF.preload(PLAYER_KICK_GLB_URL);
  useGLTF.preload(CAPTAIN_TSUBASA_GLB_URL);
  void preloadPitchBed();
}

export function scheduleExitHandoff() {
  if (handoffLocked) return;
  handoffLocked = true;
  useTunnelExitStore.getState().setBeat('fade');
  useTunnelExitStore.getState().setProgress(1);
  useGameStore.getState().setOverlayOpacity(1);
  silenceAfterTunnelExit(0.55);
  stopExitSynth(0.45);
  preloadExitBlackVoiceover();
  void preloadPitchBed();

  if (voTimer != null) window.clearTimeout(voTimer);
  voTimer = window.setTimeout(() => {
    voTimer = null;
    void playExitBlackVoiceover({ gain: 1.2 });
    // Track already skips the first 0.8s; short rise so music is with the VO.
    void startPitchBed(0.18);
  }, EXIT_BLACKOUT_HOLD_MS);

  // Parse shot GLBs after the first VO lines are on screen — not on the black cut.
  clearTimer(shotWarmTimer);
  shotWarmTimer = window.setTimeout(() => {
    shotWarmTimer = null;
    prewarmShotAssets();
  }, EXIT_BLACKOUT_HOLD_MS + 2800);

  if (handoffTimer != null) window.clearTimeout(handoffTimer);
  handoffTimer = window.setTimeout(() => {
    requestAnimationFrame(() => {
      stopExitBlackVoiceover(0.35);
      useGameStore.getState().startShot();
      useTunnelExitStore.getState().reset();
      handoffTimer = null;
    });
  }, EXIT_BLACKOUT_HOLD_MS + EXIT_BLACK_MS);
}

type Props = {
  active: boolean;
  prewarm?: boolean;
};

/** Prewarms shot assets only — jersey-back camera plate is disabled. */
export function TunnelExitCinematic({ prewarm = false }: Props) {
  useEffect(() => {
    if (prewarm) {
      preloadExitBlackVoiceover();
      void preloadPitchBed();
    }
  }, [prewarm]);
  return null;
}
